import { useState } from 'react'
import {
  useDaftarkanAkun, useMuatKredensial, usePeta,
} from '../../../domains/tenancy/api'
import { Bendera, Isian, Panel, Pilihan, Tombol } from '../../../shared/ui'

/**
 * SATU alur "Tambah akun" — menggantikan dua formulir terpisah.
 *
 * Operator menunjuk 10 September: "Akun Platform" dan "Kredensial Meta" terlihat seperti
 * dua urusan yang tidak berhubungan, padahal dari sisinya menyambungkan Instagram klien
 * adalah SATU pekerjaan.
 *
 * KEDUANYA MEMANG BUKAN HAL YANG SAMA, dan itu sebabnya ini bukan sekadar penggabungan:
 *
 *     social_accounts       akun MANA          unik GLOBAL, satu baris per akun
 *     tenant_credentials    izin BERTINDAK     satu baris aktif per PENYEDIA
 *
 * Satu token Meta melayani Instagram DAN Facebook Page. Formulir datar yang selalu
 * menanyakan token akan menyatakan "satu token per akun" — dan akun Instagram kedua akan
 * dimintai tokennya lagi, atau lebih buruk, merotasi yang sudah ada tanpa ada yang
 * bermaksud begitu.
 *
 * Jadi formulir ini MENYESUAIKAN, termasuk menyesuaikan apakah token perlu ditanyakan:
 * kalau penyedianya sudah punya kredensial aktif, ia mengatakan begitu alih-alih meminta.
 *
 * ROTASI TETAP TERPISAH. Mengganti token adalah tindakan yang disengaja, dan menaruhnya
 * di jalur yang sama dengan "tambah akun" berarti seseorang bisa merotasinya sambil
 * mengerjakan hal lain.
 */
export default function TambahAkun({ id, t }) {
  const peta = usePeta(id)
  const daftarkan = useDaftarkanAkun(id)
  const muat = useMuatKredensial(id)

  const [platform, setPlatform] = useState('instagram')
  const [akun, setAkun] = useState('')
  const [token, setToken] = useState('')

  const daftar = peta.data ?? []
  const p = daftar.find((x) => x.platform === platform)
  const terkunci = p && !p.adapter_ada
  const perluToken = p && p.adapter_ada && !p.kredensial_ada

  const sibuk = daftarkan.isPending || muat.isPending
  const galat = daftarkan.error || muat.error

  async function kirim() {
    // Akun DULU, token sesudahnya. Kalau dibalik, token tersimpan untuk tenant yang
    // ternyata gagal mendaftarkan akunnya — dan kredensial sekali-tulis yang menganggur
    // tidak bisa dibatalkan, cuma dirotasi.
    await daftarkan.mutateAsync({ platform, external_account_id: akun.trim() })
    if (perluToken && token.trim()) {
      await muat.mutateAsync({ provider: p.provider, nilai: token.trim() })
    }
    setAkun('')
    setToken('')
    peta.refetch()
  }

  return (
    <Panel judul={t('ta2.judul')} catatan={t('ta2.catatan')}>
      <div className="p-3.5 flex flex-col gap-3 max-w-[64ch]">
        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-[.07em] text-ink3">
            {t('ta2.platform')}
          </span>
          <Pilihan value={platform} onChange={(e) => setPlatform(e.target.value)}>
            {daftar.map((x) => (
              // Yang belum punya adapter TETAP MUNCUL, dan tetap bisa dipilih supaya
              // penjelasannya terbaca. Menyembunyikannya membuat operator mengira
              // fiturnya hilang; menonaktifkannya diam-diam membuatnya mengira rusak.
              <option key={x.platform} value={x.platform}>
                {x.label}{x.adapter_ada ? '' : ` — ${t('sb.kap.belum_dibangun')}`}
              </option>
            ))}
          </Pilihan>
        </label>

        {terkunci ? (
          <Bendera nada="warn">{t('ta2.terkunci', { platform: p.label })}</Bendera>
        ) : (
          <>
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-[.07em] text-ink3">
                {t('ta2.idAkun', { platform: p?.label ?? '' })}
              </span>
              <Isian
                value={akun}
                placeholder="17841400000000000"
                onChange={(e) => setAkun(e.target.value)}
              />
            </label>

            {perluToken ? (
              <label className="flex flex-col gap-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-[.07em] text-ink3">
                  {t('ta2.token', { provider: p.provider })}
                </span>
                {/* type=password dan autoComplete=off: token bukan kata sandi orang ini,
                    jadi tidak boleh ditawarkan disimpan pengelola sandi peramban. */}
                <Isian
                  type="password"
                  autoComplete="off"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                />
                <span className="text-[11.5px] text-ink3">{t('ta2.tokenCatatan')}</span>
              </label>
            ) : (
              p && (
                <Bendera nada="ok">{t('ta2.tokenSudahAda', { provider: p.provider })}</Bendera>
              )
            )}

            {galat && (
              <Bendera nada="bad" role="alert">{galat.message}</Bendera>
            )}

            <div>
              <Tombol
                disabled={!akun.trim() || sibuk || (perluToken && !token.trim())}
                onClick={kirim}
              >
                {sibuk ? t('umum.menyimpan') : t('ta2.sambungkan')}
              </Tombol>
            </div>
          </>
        )}
      </div>
    </Panel>
  )
}
